import axios from "axios";
import appconfig from "../AppConfig";

/*
 * Server-side admin login. The token from api/admin/login is kept in localStorage and sent as
 * X-Admin-Token on every API call (one axios interceptor, so the existing admin API files need no
 * changes). The server rejects admin endpoints without a valid token with 401.
 */

const KEY = "bpb.adminToken";
const HEADER = "X-Admin-Token";

export const getAdminToken = (): string | null => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};

const setAdminToken = (token: string) => {
  try {
    localStorage.setItem(KEY, token);
  } catch {
    /* private mode: login lasts for this page only */
  }
};

export const clearAdminToken = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};

let installed = false;

/** Adds the admin token to every request; a 401 from the API clears it. */
export const installAdminInterceptor = (onLoggedOut: () => void) => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const token = getAdminToken();
    if (token) {
      config.headers = { ...(config.headers || {}), [HEADER]: token };
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401 && getAdminToken()) {
        clearAdminToken();
        onLoggedOut();
      }
      return Promise.reject(error);
    }
  );
};

/** Returns null on success, otherwise a message for the login page. */
export const adminLogin = async (password: string): Promise<string | null> => {
  try {
    const response = await axios.post<any>(
      "admin/login",
      { password },
      { baseURL: appconfig.baseURL }
    );
    setAdminToken(response.data.token);
    return null;
  } catch (err: any) {
    return err?.response?.data?.error ?? "Prijava nije uspela. Proverite internet vezu.";
  }
};

/** True if a stored token is still accepted by the server. */
export const checkAdminSession = async (): Promise<boolean> => {
  if (!getAdminToken()) return false;
  try {
    await axios.get<any>("admin/check", { baseURL: appconfig.baseURL });
    return true;
  } catch {
    return false;
  }
};
